import React, { useState, useEffect } from 'react';
import { styled, Typography, Box, Fab, Popover } from '@mui/material';
import { List, ListItemButton, ListItemText, ListItemIcon } from '@mui/material';
import { Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LaunchIcon from '@mui/icons-material/Launch';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useHistory, useRouteMatch } from 'react-router-dom';
import fetchExpresso from 'utility/fetchExpresso';
import { SearchBar } from 'components/ui/inputs';
import { normalizeCharacters } from 'utility/strings';
import { ErrorPage, LoadingPage } from 'components/ui/states';

export default function AdminMenu(props) {
    const { hostId, hostName, navigatorVisible } = props;

    const [connectId, setConnectId] = useState(null);

    const [hostPickerAnchor, setHostPickerAnchor] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);

    const onboardingPage = useRouteMatch({ path: '/settings/onboarding' });
    const connectPage = useRouteMatch({ path: '/admin/hosts/:id/host_connect' });

    const history = useHistory();

    useEffect(() => {
        fetchExpresso(`/apiv2/hosts/${hostId}`)
            .then((data) => data.json())
            .then((host) => setConnectId(host.connect_id))
            .catch(() => null);
    }, [hostId]);

    const searchHost = (e) => {
        setHostPickerAnchor(e.currentTarget);
    };

    const changeHost = (id, name) => {
        history.push(`/host_redirect?host_id=${id}&host_name=${encodeURIComponent(name)}`);
        setHostPickerAnchor(null);
    };

    const openMenu = (e) => setAnchorEl(e.currentTarget);
    const closeMenu = () => setAnchorEl(null);

    const viewOnboarding = () => {
        history.push('/settings/onboarding');
        closeMenu();
    };
    const viewConnectDetails = () => {
        history.push(`/admin/hosts/${hostId}/host_connect?host=${encodeURIComponent(hostName)}`);
        closeMenu();
    };

    let dashboardUrl = null;

    if (connectId) {
        dashboardUrl =
            process.env.REACT_APP_ENV === 'production'
                ? `https://dashboard.stripe.com/connect/accounts/${connectId}`
                : `https://dashboard.stripe.com/test/connect/accounts/${connectId}`;
    }

    return (
        <Container sx={{ bottom: { xs: navigatorVisible ? 65 : 10, md: 10 } }}>
            <HostFab onClick={searchHost}>{hostName}</HostFab>

            <MenuFab onClick={openMenu}>
                <MenuIcon />
            </MenuFab>

            <Menu
                id="admin-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={closeMenu}
            >
                <MenuItem onClick={viewOnboarding} disabled={Boolean(onboardingPage)}>
                    Host - Complete Setup
                </MenuItem>
                <MenuItem onClick={viewConnectDetails} disabled={Boolean(connectPage)}>
                    Admin - Connect Info
                </MenuItem>
                <MenuItem
                    onClick={closeMenu}
                    disabled={!dashboardUrl}
                    component={'a'}
                    href={dashboardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Typography>Stripe</Typography>
                    <ExternalLinkIcon />
                </MenuItem>
            </Menu>

            <HostPopover
                open={Boolean(hostPickerAnchor)}
                anchorEl={hostPickerAnchor}
                onClose={() => setHostPickerAnchor(null)}
            >
                <HostPicker hostId={hostId} onSelectHost={changeHost} />
            </HostPopover>
        </Container>
    );
}

function HostPicker(props) {
    const { hostId, onSelectHost } = props;

    const [search, setSearch] = useState('');

    const [state, setState] = useState({
        hosts: [],
        status: 'loading', // loading, error, success
    });

    useEffect(() => {
        fetchExpresso(`/api/auths/host_select`)
            .then(async (res) => {
                if (res.status !== 200) throw new Error();

                const hosts = await res.json();

                setState((s) => ({ ...s, hosts: hosts, status: 'success' }));
            })
            .catch(() => setState((s) => ({ ...s, status: 'error' })));
    }, []);

    const handleHostSelect = (h) => {
        onSelectHost(h.host_id, h.host_name);

        // Save selected host to the 'recent hosts' list
        addRecentHost(h);
    };

    let results = [];

    let status = state.status;

    if (status === 'success') {
        // User started search - Show hosts that match the search query
        if (search.trim()) {
            const searchStr = normalizeCharacters(search.trim()); // Normalize string to remove accents and diacritics

            const searchRegex = new RegExp(
                searchStr.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'),
                'i',
            );

            results = state.hosts.filter((h) => {
                const hostName = normalizeCharacters(h.host_name);
                return searchRegex.test(hostName);
            });
        }

        // User has not started search - Show recent hosts instead
        else {
            const recents = getRecentHosts();

            for (const r of recents) {
                for (const h of state.hosts) {
                    // Push recent hosts to result list
                    // Ignores the current host since you can't switch to them
                    if (r.host_id === h.host_id && h.host_id !== hostId) {
                        results.push(h);
                    }
                }
            }
        }
    }

    // When user hits 'enter' select the first host displayed on the list

    const onKeyPress = (e) => {
        if (e.key === 'Enter' && results.length > 0) {
            handleHostSelect(results[0]);
        }
    };

    return (
        <Box width={400} height={300} display="flex" flexDirection={'column'}>
            <Box flex={1} style={{ overflowX: 'auto' }}>
                {status === 'loading' && <LoadingPage />}
                {status === 'error' && <ErrorPage message="Unable to load hosts" />}
                {status === 'success' && results.length === 0 && search.length > 0 && (
                    <ErrorPage message="No results found" />
                )}
                <HostList>
                    {status === 'success' &&
                        results.map((host) => (
                            <ListItemButton
                                key={host.host_id}
                                onClick={() => handleHostSelect(host)}
                            >
                                {host.live_event_count === 1 ? (
                                    <>
                                        <ListItemText disableTypography>
                                            <LiveHost>{host.host_name}</LiveHost>
                                        </ListItemText>
                                        <ListItemIcon>
                                            <LiveIcon />
                                        </ListItemIcon>
                                    </>
                                ) : (
                                    <ListItemText>{host.host_name}</ListItemText>
                                )}
                            </ListItemButton>
                        ))}
                </HostList>
            </Box>

            <SearchBar
                autoFocus
                value={search}
                onChange={setSearch}
                placeholder="Find Host"
                onKeyPress={onKeyPress}
            />
        </Box>
    );
}

function getRecentHosts() {
    try {
        const recents = JSON.parse(localStorage.getItem('admin_recent_hosts'));

        if (Array.isArray(recents)) {
            return recents;
        } else {
            throw new Error();
        }
    } catch (error) {
        return [];
    }
}

export function addRecentHost(host) {
    try {
        let recents = getRecentHosts();

        const existingHost = recents.findIndex((h) => h.host_id === host.host_id);

        if (existingHost === -1) {
            // Add host to front of recents list
            // Max 6 (5 will be shown since we don't show the current host under recents)
            recents = recents.slice(0, 5);
            recents.unshift(host);
        } else {
            // Host already exists. Move it to the front of the list.
            recents.splice(existingHost, 1);
            recents.unshift(host);
        }

        localStorage.setItem('admin_recent_hosts', JSON.stringify(recents));

        return { error: false };
    } catch (error) {
        return { error: true };
    }
}

const Container = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    position: 'fixed',
    right: 10,
    zIndex: 1,
}));

const HostFab = styled(Fab)(({ theme }) => ({
    marginRight: theme.spacing(1),
    color: theme.palette.grey[600],
}));
HostFab.defaultProps = { size: 'small', variant: 'extended' };

const MenuFab = styled(Fab)(({ theme }) => ({
    color: theme.palette.grey[600],
}));
MenuFab.defaultProps = { size: 'small', variant: 'circular' };

const ExternalLinkIcon = styled(LaunchIcon)(({ theme }) => ({
    color: theme.palette.info.main,
    fontSize: 16,
    marginLeft: theme.spacing(1),
}));

const HostList = styled(List)(({ theme }) => ({
    width: '100%',
}));

const LiveHost = styled(Typography)(({ theme }) => ({
    color: theme.palette.success.main,
    fontWeight: 'bold',
}));

const LiveIcon = styled(EventAvailableIcon)(({ theme }) => ({
    color: theme.palette.success.main,
}));

const HostPopover = styled(Popover)(({ theme }) => ({}));
HostPopover.defaultProps = {
    anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
    },
    transformOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
    },
};
